import React, { useState, useEffect } from 'react';
import { 
  UserPlusIcon, 
  ArrowUpIcon, 
  ArrowRightIcon,
  ArrowLeftIcon, 
  CheckCircleIcon,
  XCircleIcon,
  ChatBubbleLeftIcon,
  PlayIcon,
  DocumentMagnifyingGlassIcon,
  ExclamationTriangleIcon,
  ClockIcon,
  UserIcon,
  CogIcon
} from '@heroicons/react/24/outline';
import ticketService from '../../services/ticketService';

const AdminTicketActions = ({ ticket, onAction, loading = false, onRefresh, userContext: propUserContext }) => {
  const [userContext, setUserContext] = useState({});
  const [availableActions, setAvailableActions] = useState([]);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showEscalateModal, setShowEscalateModal] = useState(false);
  const [showForwardModal, setShowForwardModal] = useState(false);
  const [showResolveModal, setShowResolveModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showAdminReassignModal, setShowAdminReassignModal] = useState(false);
  
  // FIXED: Initialize as empty arrays to prevent .map errors
  const [selectedAgent, setSelectedAgent] = useState('');
  const [availableAgents, setAvailableAgents] = useState([]);
  const [escalationAgents, setEscalationAgents] = useState([]);
  const [actionLoading, setActionLoading] = useState(false);
  
  const [remarks, setRemarks] = useState('');
  const [escalationReason, setEscalationReason] = useState('');
  const [escalationType, setEscalationType] = useState('auto');
  const [forwardReason, setForwardReason] = useState('');
  const [resolutionNotes, setResolutionNotes] = useState('');
  const [reassignReason, setReassignReason] = useState('');
  const [isReturnForward, setIsReturnForward] = useState(false); // NEW: Return forward state
  
  const [confirmAction, setConfirmAction] = useState(null);

  useEffect(() => {
    // Use context from props (backend) first, then fallback to ticketService
    let context = {};
    
    if (propUserContext && Object.keys(propUserContext).length > 0) {
      console.log('Using user context from props (backend):', propUserContext);
      context = propUserContext;
    } else {
      console.log('Falling back to ticketService context');
      context = ticketService.getUserContext();
    }
    
    console.log('AdminTicketActions - Final user context:', context);
    setUserContext(context);
    
    // Set available actions
    const actions = ticketService.getAvailableActions(ticket?.id) || [];
    setAvailableActions(actions);
    
  }, [ticket?.id, propUserContext]);

  // FIXED: Enhanced fetchAvailableExperts with proper error handling
  const fetchAvailableExperts = async () => {
    try {
      const response = await ticketService.getAvailableExperts(ticket.id);
      console.log('Available experts response:', response);
      
      // FIXED: Ensure we always set arrays, even if API returns undefined/null
      if (response?.data) {
        setAvailableAgents(Array.isArray(response.data.available_experts) ? response.data.available_experts : []);
        setEscalationAgents(Array.isArray(response.data.escalation_experts) ? response.data.escalation_experts : []);
        
        // Alternative: If escalation_experts doesn't exist, use available_experts
        if (!response.data.escalation_experts && response.data.available_experts) {
          setEscalationAgents(Array.isArray(response.data.available_experts) ? response.data.available_experts : []);
        }
      } else {
        // Fallback: Set empty arrays if no data
        setAvailableAgents([]);
        setEscalationAgents([]);
      }
    } catch (error) {
      console.error('Failed to fetch experts:', error);
      alert('Failed to load available agents');
      // FIXED: Always set arrays on error
      setAvailableAgents([]);
      setEscalationAgents([]);
    }
  };

  // Use userContext from backend with proper fallback
  const hasPermission = (permission) => {
    console.log(`Checking permission: ${permission}`, {
      userContext,
      canKey: `can_${permission}`,
      canValue: userContext[`can_${permission}`],
      isAdmin: userContext.is_admin
    });
    
    return userContext[`can_${permission}`] === true || userContext.is_admin === true;
  };

  const canPerformAction = (action) => {
    return availableActions.includes(action) || userContext.is_admin === true;
  };

  // Custom confirm function
  const showConfirmation = (message, onConfirm) => {
    setConfirmAction({
      message,
      onConfirm
    });
    setShowConfirmModal(true);
  };

  // Assignment Handler
  const handleAssignTicket = async () => {
    if (!hasPermission('assign')) {
      alert('You do not have permission to assign tickets.');
      return;
    }
    
    await fetchAvailableExperts();
    setShowAssignModal(true);
  };

  const confirmAssignment = async () => {
    if (!selectedAgent) {
      alert('Please select an agent');
      return;
    }

    setActionLoading(true);
    try {
      const assignmentData = {
        agent_id: parseInt(selectedAgent),
        remarks: remarks || null,
        force_assign: false
      };

      console.log('Sending assignment data:', assignmentData);
      const response = await ticketService.assignByExpertise(ticket.id, assignmentData);
      console.log('Assignment response:', response);

      if (response?.code === 'expertise_mismatch') {
        showConfirmation(
          'Agent does not have expertise in this category. Force assign anyway?',
          async () => {
            assignmentData.force_assign = true;
            await ticketService.assignByExpertise(ticket.id, assignmentData);
            setShowAssignModal(false);
            setSelectedAgent('');
            setRemarks('');
            alert('Ticket assigned successfully');
            onAction('assigned');
            if (onRefresh) onRefresh();
          }
        );
        setActionLoading(false);
        return;
      }

      setShowAssignModal(false);
      setSelectedAgent('');
      setRemarks('');
      alert('Ticket assigned successfully');
      onAction('assigned');
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error('Assignment failed:', error);
      alert(error.message || 'Failed to assign ticket');
    } finally {
      setActionLoading(false);
    }
  };

  // UPDATED: Enhanced Escalation Handler with Auto/Manual Options
  const handleEscalateTicket = async () => {
    if (!hasPermission('escalate')) {
      alert('You do not have permission to escalate tickets.');
      return;
    }
    
    // Fetch escalation agents for manual escalation option
    await fetchAvailableExperts();
    setShowEscalateModal(true);
  };

  const confirmEscalation = async () => {
    if (!escalationReason.trim()) {
      alert('Please provide an escalation reason');
      return;
    }

    if (escalationType === 'manual' && !selectedAgent) {
      alert('Please select an agent for manual escalation');
      return;
    }

    setActionLoading(true);
    try {
      const escalationData = {
        escalation_reason: escalationReason,
        escalation_type: escalationType,
        urgent: ticket.priority === 'critical'
      };

      if (escalationType === 'manual') {
        escalationData.agent_id = parseInt(selectedAgent);
      }

      console.log('Sending escalation data:', escalationData);
      await ticketService.escalateTicket(ticket.id, escalationData);

      setShowEscalateModal(false);
      setEscalationReason('');
      setEscalationType('auto');
      setSelectedAgent('');
      
      const escalationTypeLabel = escalationType === 'auto' ? 'auto-escalated' : 'manually escalated';
      alert(`Ticket ${escalationTypeLabel} successfully`);
      
      onAction('escalated');
      
      // Redirect after escalation
      setTimeout(() => {
        window.location.href = '/tickets';
      }, 2000);
      
    } catch (error) {
      console.error('Escalation failed:', error);
      alert(error.message || 'Failed to escalate ticket');
    } finally {
      setActionLoading(false);
    }
  };

  // NEW: Admin Reassignment Handler for Escalated Tickets
  const handleAdminReassignEscalated = async () => {
    if (!userContext.is_admin) {
      alert('Admin access required for ticket reassignment.');
      return;
    }
    
    if (ticket.status !== 'escalated') {
      alert('Ticket must be escalated to perform admin reassignment.');
      return;
    }
    
    await fetchAvailableExperts();
    setShowAdminReassignModal(true);
  };

  const confirmAdminReassign = async () => {
    if (!selectedAgent) {
      alert('Please select an agent');
      return;
    }
    
    if (!reassignReason.trim()) {
      alert('Please provide a reason for reassignment');
      return;
    }

    setActionLoading(true);
    try {
      const reassignData = {
        agent_id: parseInt(selectedAgent),
        reassign_reason: reassignReason
      };

      console.log('Sending admin reassignment data:', reassignData);
      await ticketService.adminReassignEscalated(ticket.id, reassignData);

      setShowAdminReassignModal(false);
      setSelectedAgent('');
      setReassignReason('');
      alert('Escalated ticket reassigned successfully');
      
      onAction('admin_reassigned');
      if (onRefresh) onRefresh();
      
    } catch (error) {
      console.error('Admin reassignment failed:', error);
      alert(error.message || 'Failed to reassign ticket');
    } finally {
      setActionLoading(false);
    }
  };

  // UPDATED: Forward Handler with Return Forward Support
  const handleForwardTicket = async () => {
    if (!hasPermission('forward')) {
      alert('You do not have permission to forward tickets.');
      return;
    }
    
    await fetchAvailableExperts();
    setShowForwardModal(true);
  };

  // UPDATED: Enhanced confirmForward with return forward support
  const confirmForward = async () => {
    if (!selectedAgent) {
      alert('Please select an agent to forward to');
      return;
    }
    if (!forwardReason.trim()) {
      alert(`Please provide a reason for ${isReturnForward ? 'returning' : 'forwarding'}`);
      return;
    }

    setActionLoading(true);
    try {
      // UPDATED: Include is_return_forward in the request
      await ticketService.forwardTicket(ticket.id, {
        agent_id: parseInt(selectedAgent),
        forward_reason: forwardReason,
        is_return_forward: isReturnForward // NEW: Send return forward flag
      });

      setShowForwardModal(false);
      setSelectedAgent('');
      setForwardReason('');
      setIsReturnForward(false); // NEW: Reset return forward state
      
      const actionText = isReturnForward ? 'returned' : 'forwarded';
      alert(`Ticket ${actionText} successfully`);
      
      // UPDATED: Send correct action type
      onAction(isReturnForward ? 'returned' : 'forwarded');
      
      // Give a small delay then redirect to tickets list
      setTimeout(() => {
        window.location.href = '/tickets';
      }, 2000);
      
    } catch (error) {
      console.error('Forward failed:', error);
      const actionText = isReturnForward ? 'return' : 'forward';
      alert(error.message || `Failed to ${actionText} ticket`);
    } finally {
      setActionLoading(false);
    }
  };

  // Start Working Handler
  const handleStartWorking = async () => {
    console.log('Start working check:', {
      canStartWorking: userContext.can_start_working,
      canPerformAction: canPerformAction('start_working'),
      hasPermission: hasPermission('respond')
    });
    
    if (!hasPermission('respond') && !userContext.can_start_working) {
      alert('You cannot start working on this ticket.');
      return;
    }
    
    setActionLoading(true);
    try {
      await ticketService.startWorking(ticket.id);
      alert('Started working on ticket');
      onAction('start_working');
      if (onRefresh) onRefresh();
    } catch (error) {
      alert(error.message || 'Failed to start working');
    } finally {
      setActionLoading(false);
    }
  };

  // Resolve Handler
  const handleMarkSolved = () => {
    if (!hasPermission('solve')) {
      alert('You do not have permission to resolve tickets.');
      return;
    }
    setShowResolveModal(true);
  };

  const confirmResolve = async () => {
    if (!resolutionNotes.trim()) {
      alert('Please provide resolution notes');
      return;
    }

    setActionLoading(true);
    try {
      await ticketService.markSolved(ticket.id, {
        resolution_notes: resolutionNotes,
        solution_category: 'resolved'
      });
      
      setShowResolveModal(false);
      setResolutionNotes('');
      alert('Ticket marked as resolved');
      onAction('resolved');
      if (onRefresh) onRefresh();
    } catch (error) {
      alert(error.message || 'Failed to resolve ticket');
    } finally {
      setActionLoading(false);
    }
  };

  // Close Handler
  const handleCloseTicket = async () => {
    if (!hasPermission('close')) {
      alert('You do not have permission to close tickets.');
      return;
    }

    if (ticket.status !== 'resolved') {
      alert('Ticket must be resolved before it can be closed.');
      return;
    }

    showConfirmation(
      'Are you sure you want to close this ticket? This action cannot be undone.',
      async () => {
        setActionLoading(true);
        try {
          await ticketService.closeTicket(ticket.id);
          alert('Ticket closed successfully');
          onAction('closed');
          if (onRefresh) onRefresh();
        } catch (error) {
          alert(error.message || 'Failed to close ticket');
        } finally {
          setActionLoading(false);
        }
      }
    );
  };

  // View Customer Details
  const handleViewCustomerDetails = () => {
    const customer = ticket.creator;
    if (customer) {
      alert(`Customer Details:\n
Name: ${customer.name}
Email: ${customer.email}
Phone: ${customer.phone || 'N/A'}
Company: ${customer.company || 'N/A'}`);
      onAction('view_customer', customer);
    }
  };

  // Open Chat
  const handleOpenChat = () => {
    if (ticket.agent_id) {
      onAction('open_chat');
    } else {
      alert('Chat is only available after ticket is assigned to an agent');
    }
  };

  // Check if user should see actions center
  const isAssignedAgent = ticket.agent_id === userContext.user_id;

  const shouldShowActionsCenter = () => {
    console.log('Checking if should show actions center:', {
      isAdmin: userContext.is_admin,
      isAgent: userContext.is_agent,
      hasAnyPermission: hasPermission('assign') || hasPermission('escalate') || 
                       hasPermission('forward') || hasPermission('solve') || 
                       hasPermission('close'),
      isAssignedAgent
    });
    
    return userContext.is_admin || userContext.is_agent || 
           hasPermission('assign') || hasPermission('escalate') || 
           hasPermission('forward') || hasPermission('solve') || 
           hasPermission('close') || isAssignedAgent;
  };

  // Show customer-friendly section instead of "No Actions Available"
  if (!shouldShowActionsCenter()) {
    return (
      <div className="bg-gradient-to-br from-white to-blue-50 shadow-lg border border-blue-100 rounded-2xl overflow-hidden">
        {/* Header Section */}
        <div className="bg-gradient-to-r from-blue-500 to-indigo-600 p-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-xl font-bold text-white mb-2">
                Ticket Information
              </h3>
              <p className="text-blue-100 text-sm">
                Track your support request status and updates
              </p>
            </div>
            <div className="bg-white/20 rounded-lg p-3">
              <DocumentMagnifyingGlassIcon className="h-8 w-8 text-white" />
            </div>
          </div>
        </div>

        {/* Customer Status Section */}
        <div className="p-6 space-y-4">
          <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
            <div className="flex items-center mb-3">
              <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center mr-3">
                <ClockIcon className="h-4 w-4 text-blue-600" />
              </div>
              <h4 className="font-semibold text-gray-900">Current Status</h4>
            </div>
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Ticket Status:</span>
                <span className="text-sm font-medium text-gray-900 capitalize">{ticket.status?.replace('_', ' ')}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Priority:</span>
                <span className="text-sm font-medium text-gray-900 capitalize">{ticket.priority}</span>
              </div>
              {ticket.agent && (
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Assigned to:</span>
                  <span className="text-sm font-medium text-gray-900">{ticket.agent.name}</span>
                </div>
              )}
            </div>
          </div>

          {/* What You Can Do Section */}
          <div className="bg-white border border-emerald-200 rounded-xl p-4 shadow-sm">
            <div className="flex items-center mb-3">
              <div className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center mr-3">
                <ChatBubbleLeftIcon className="h-4 w-4 text-emerald-600" />
              </div>
              <h4 className="font-semibold text-gray-900">What You Can Do</h4>
            </div>
            <div className="space-y-3">
              {userContext.can_comment && (
                <div className="flex items-center text-sm text-gray-700">
                  <CheckCircleIcon className="h-4 w-4 text-emerald-500 mr-2" />
                  Add comments and additional information
                </div>
              )}
              <div className="flex items-center text-sm text-gray-700">
                <CheckCircleIcon className="h-4 w-4 text-emerald-500 mr-2" />
                Receive updates on ticket progress
              </div>
              {ticket.agent_id && (
                <div className="flex items-center text-sm text-gray-700">
                  <CheckCircleIcon className="h-4 w-4 text-emerald-500 mr-2" />
                  Chat with your assigned agent
                </div>
              )}
              <div className="flex items-center text-sm text-gray-700">
                <CheckCircleIcon className="h-4 w-4 text-emerald-500 mr-2" />
                View all communication history
              </div>
            </div>
          </div>

          {/* Quick Actions for Customer */}
          <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
            <div className="flex items-center mb-3">
              <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center mr-3">
                <UserIcon className="h-4 w-4 text-gray-600" />
              </div>
              <h4 className="font-semibold text-gray-900">Quick Actions</h4>
            </div>
            <div className="space-y-3">
              {userContext.can_comment && (
                <button
                  onClick={() => onAction('add_comment')}
                  className="w-full inline-flex items-center justify-center px-4 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <ChatBubbleLeftIcon className="h-4 w-4 mr-2" />
                  Add Comment
                </button>
              )}
              {ticket.agent_id && (
                <button
                  onClick={handleOpenChat}
                  className="w-full inline-flex items-center justify-center px-4 py-2 bg-emerald-600 text-white font-medium rounded-lg hover:bg-emerald-700 transition-colors"
                >
                  <ChatBubbleLeftIcon className="h-4 w-4 mr-2" />
                  Start Chat
                </button>
              )}
            </div>
          </div>

          {/* Help Section */}
          <div className="bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200 rounded-xl p-4">
            <div className="flex items-center mb-2">
              <div className="w-6 h-6 bg-purple-100 rounded-full flex items-center justify-center mr-2">
                <ExclamationTriangleIcon className="h-3 w-3 text-purple-600" />
              </div>
              <h5 className="font-medium text-purple-900">Need Help?</h5>
            </div>
            <p className="text-xs text-purple-700 leading-relaxed">
              Our support team is working on your request. You'll receive updates automatically. 
              Feel free to add comments if you have additional information to share.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Get access level description
  const getAccessDescription = () => {
    switch (userContext.access_level) {
      case 'all_tickets':
        return 'Full System Access';
      case 'assigned_tickets':
        return 'Agent Access';
      case 'own_tickets':
        return 'Customer Access';
      default:
        return 'Limited Access';
    }
  };

  return (
    <div className="bg-gradient-to-br from-white to-gray-50 shadow-xl border border-gray-100 rounded-2xl overflow-hidden">
      {/* Header Section */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-xl font-bold text-white mb-2">
              Ticket Actions Center
            </h3>
            <p className="text-blue-100 text-sm">
              Manage and process ticket operations efficiently
            </p>
          </div>
          <div className="bg-white/20 rounded-lg p-3">
            <DocumentMagnifyingGlassIcon className="h-8 w-8 text-white" />
          </div>
        </div>
      </div>

      {/* User Role Information */}
      <div className="px-6 py-4 bg-gradient-to-r from-blue-50 to-purple-50 border-b border-gray-100">
        <div className="flex items-center space-x-3">
          <div className="flex-shrink-0">
            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
              <UserPlusIcon className="h-5 w-5 text-blue-600" />
            </div>
          </div>
          <div>
            <p className="text-sm font-medium text-gray-900">
              Current Role: {userContext.role_display_name || 'Loading...'}
            </p>
            <p className="text-xs text-gray-600">
              {getAccessDescription()}
            </p>
          </div>
        </div>
      </div>

      {/* Actions Content */}
      <div className="p-6 space-y-6">
        {/* Assignment Actions - Only for users with assign permission */}
        {hasPermission('assign') && ticket.status === 'open' && (
          <div className="bg-white border border-blue-200 rounded-xl p-4 shadow-sm">
            <div className="flex items-center mb-3">
              <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center mr-3">
                <UserPlusIcon className="h-4 w-4 text-blue-600" />
              </div>
              <h4 className="font-semibold text-gray-900">Assignment Management</h4>
            </div>
            <p className="text-sm text-gray-600 mb-4">Assign this ticket to an available agent based on expertise</p>
            <button
              onClick={handleAssignTicket}
              disabled={loading || actionLoading}
              className="inline-flex items-center px-4 py-2.5 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 focus:ring-4 focus:ring-blue-200 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <UserPlusIcon className="h-4 w-4 mr-2" />
              Assign Ticket
            </button>
          </div>
        )}

        {/* Agent Actions - Only for assigned agent */}
        {isAssignedAgent && (
          <div className="bg-white border border-green-200 rounded-xl p-4 shadow-sm">
            <div className="flex items-center mb-3">
              <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center mr-3">
                <PlayIcon className="h-4 w-4 text-green-600" />
              </div>
              <h4 className="font-semibold text-gray-900">My Actions</h4>
            </div>
            <p className="text-sm text-gray-600 mb-4">Actions available for the assigned agent</p>
            <div className="flex flex-wrap gap-3">
              {ticket.status === 'assigned' && (hasPermission('respond') || userContext.can_start_working) && (
                <button
                  onClick={handleStartWorking}
                  disabled={loading || actionLoading}
                  className="inline-flex items-center px-4 py-2.5 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 focus:ring-4 focus:ring-green-200 transition-all duration-200"
                >
                  <PlayIcon className="h-4 w-4 mr-2" />
                  Start Working
                </button>
              )}

              {ticket.agent_id && (
                <button
                  onClick={handleOpenChat}
                  disabled={loading}
                  className="inline-flex items-center px-4 py-2.5 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 focus:ring-4 focus:ring-blue-200 transition-all duration-200"
                >
                  <ChatBubbleLeftIcon className="h-4 w-4 mr-2" />
                  Open Chat
                </button>
              )}

              <button
                onClick={() => onAction('add_comment')}
                disabled={loading}
                className="inline-flex items-center px-4 py-2.5 bg-indigo-600 text-white font-medium rounded-lg hover:bg-indigo-700 focus:ring-4 focus:ring-indigo-200 transition-all duration-200"
              >
                <ChatBubbleLeftIcon className="h-4 w-4 mr-2" />
                Add Comment
              </button>
            </div>
          </div>
        )}

        {/* Transfer Actions - Only for users with escalate/forward permissions */}
        {(hasPermission('escalate') || hasPermission('forward')) && 
         !['closed', 'resolved'].includes(ticket.status) && (
          <div className="bg-white border border-orange-200 rounded-xl p-4 shadow-sm">
            <div className="flex items-center mb-3">
              <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center mr-3">
                <ArrowUpIcon className="h-4 w-4 text-orange-600" />
              </div>
              <h4 className="font-semibold text-gray-900">Transfer Operations</h4>
            </div>
            <p className="text-sm text-gray-600 mb-4">Escalate or forward tickets to appropriate channels</p>
            <div className="flex flex-wrap gap-3">
              {hasPermission('escalate') && (
                <button
                  onClick={handleEscalateTicket}
                  disabled={loading || actionLoading}
                  className="inline-flex items-center px-4 py-2.5 bg-orange-600 text-white font-medium rounded-lg hover:bg-orange-700 focus:ring-4 focus:ring-orange-200 transition-all duration-200"
                >
                  <ArrowUpIcon className="h-4 w-4 mr-2" />
                  Escalate
                </button>
              )}

              {hasPermission('forward') && (
                <button
                  onClick={handleForwardTicket}
                  disabled={loading || actionLoading}
                  className="inline-flex items-center px-4 py-2.5 bg-purple-600 text-white font-medium rounded-lg hover:bg-purple-700 focus:ring-4 focus:ring-purple-200 transition-all duration-200"
                >
                  {/* UPDATED: Icon changes based on return forward context */}
                  <ArrowRightIcon className="h-4 w-4 mr-2" />
                  Forward
                </button>
              )}
            </div>
            </div>
        )}

        {/* NEW: Admin Reassignment for Escalated Tickets */}
        {userContext.is_admin && ticket.status === 'escalated' && (
          <div className="bg-white border border-purple-200 rounded-xl p-4 shadow-sm">
            <div className="flex items-center mb-3">
              <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center mr-3">
                <CogIcon className="h-4 w-4 text-purple-600" />
              </div>
              <h4 className="font-semibold text-gray-900">Admin Override</h4>
            </div>
            <p className="text-sm text-gray-600 mb-4">Reassign this escalated ticket to a different agent</p>
            <button
              onClick={handleAdminReassignEscalated}
              disabled={loading || actionLoading}
              className="inline-flex items-center px-4 py-2.5 bg-purple-600 text-white font-medium rounded-lg hover:bg-purple-700 focus:ring-4 focus:ring-purple-200 transition-all duration-200"
            >
              <UserPlusIcon className="h-4 w-4 mr-2" />
              Admin Reassign
            </button>
          </div>
        )}

        {/* Resolution Actions - Only for users with solve/close permissions */}
        {(hasPermission('solve') || hasPermission('close')) && (
          <div className="bg-white border border-emerald-200 rounded-xl p-4 shadow-sm">
            <div className="flex items-center mb-3">
              <div className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center mr-3">
                <CheckCircleIcon className="h-4 w-4 text-emerald-600" />
              </div>
              <h4 className="font-semibold text-gray-900">Resolution Management</h4>
            </div>
            <p className="text-sm text-gray-600 mb-4">Mark tickets as resolved or close completed tickets</p>
            <div className="flex flex-wrap gap-3">
              {hasPermission('solve') && ['in_progress', 'assigned', 'escalated'].includes(ticket.status) && (
                <button
                  onClick={handleMarkSolved}
                  disabled={loading || actionLoading}
                  className="inline-flex items-center px-4 py-2.5 bg-emerald-600 text-white font-medium rounded-lg hover:bg-emerald-700 focus:ring-4 focus:ring-emerald-200 transition-all duration-200"
                >
                  <CheckCircleIcon className="h-4 w-4 mr-2" />
                  Mark as Solved
                </button>
              )}

              {hasPermission('close') && ticket.status === 'resolved' && (
                <button
                  onClick={handleCloseTicket}
                  disabled={loading || actionLoading}
                  className="inline-flex items-center px-4 py-2.5 bg-gray-600 text-white font-medium rounded-lg hover:bg-gray-700 focus:ring-4 focus:ring-gray-200 transition-all duration-200"
                >
                  <XCircleIcon className="h-4 w-4 mr-2" />
                  Close Ticket
                </button>
              )}
            </div>
          </div>
        )}

        {/* Customer Info */}
        {ticket.creator && (
          <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
            <div className="flex items-center mb-3">
              <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center mr-3">
                <DocumentMagnifyingGlassIcon className="h-4 w-4 text-gray-600" />
              </div>
              <h4 className="font-semibold text-gray-900">Customer Information</h4>
            </div>
            <p className="text-sm text-gray-600 mb-4">View detailed customer information and contact details</p>
            <button
              onClick={handleViewCustomerDetails}
              disabled={loading}
              className="inline-flex items-center px-4 py-2.5 bg-gray-600 text-white font-medium rounded-lg hover:bg-gray-700 focus:ring-4 focus:ring-gray-200 transition-all duration-200"
            >
              <DocumentMagnifyingGlassIcon className="h-4 w-4 mr-2" />
              View Customer Details
            </button>
          </div>
        )}
      </div>

      {/* All Modal Components */}
      
      {/* Confirmation Modal */}
      {showConfirmModal && confirmAction && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl p-6 max-w-md w-full shadow-2xl">
            <div className="flex items-center mb-4">
              <div className="w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center mr-3">
                <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Confirm Action</h3>
            </div>
            
            <p className="text-gray-600 mb-6">{confirmAction.message}</p>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowConfirmModal(false);
                  setConfirmAction(null);
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  confirmAction.onConfirm();
                  setShowConfirmModal(false);
                  setConfirmAction(null);
                }}
                className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 font-medium transition-colors"
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Assign Modal */}
      {showAssignModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl p-6 max-w-md w-full shadow-2xl">
            <h3 className="text-xl font-semibold mb-6 text-gray-900">Assign Ticket</h3>
            
            <div className="mb-5">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Select Agent
              </label>
              <select
                value={selectedAgent}
                onChange={(e) => setSelectedAgent(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-4 focus:ring-blue-200 focus:border-blue-500 transition-all"
                disabled={actionLoading}
              >
                <option value="">-- Select Agent --</option>
                {Array.isArray(availableAgents) && availableAgents.map(agent => (
                  <option key={agent.id} value={agent.id}>
                    {agent.name} (Level {agent.staff_level})
                    {agent.current_workload && ` - ${agent.current_workload} tickets`}
                  </option>
                ))}
              </select>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Remarks (optional)
              </label>
              <textarea
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-4 focus:ring-blue-200 focus:border-blue-500 transition-all"
                rows={3}
                disabled={actionLoading}
                placeholder="Add any additional notes..."
              />
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowAssignModal(false);
                  setSelectedAgent('');
                  setRemarks('');
                }}
                disabled={actionLoading}
                className="px-5 py-2.5 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmAssignment}
                disabled={actionLoading || !selectedAgent}
                className="px-5 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 font-medium transition-colors"
              >
                {actionLoading ? 'Assigning...' : 'Assign'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* UPDATED: Enhanced Escalate Modal with Auto/Manual Options */}
      {showEscalateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl p-6 max-w-lg w-full shadow-2xl">
            <h3 className="text-xl font-semibold mb-6 text-gray-900">Escalate Ticket</h3>
            
            {/* Escalation Type Selection */}
            <div className="mb-5">
              <label className="block text-sm font-semibold text-gray-700 mb-3">
                Escalation Type
              </label>
              <div className="space-y-3">
                <label className="flex items-center p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                  <input
                    type="radio"
                    value="auto"
                    checked={escalationType === 'auto'}
                    onChange={(e) => setEscalationType(e.target.value)}
                    className="h-4 w-4 text-orange-600 focus:ring-orange-500"
                    disabled={actionLoading}
                  />
                  <span className="ml-3 flex-1">
                    <span className="text-sm font-medium text-gray-900 block">Auto Escalation</span>
                    <span className="text-xs text-gray-500">System automatically assigns to next level agent based on workload and expertise</span>
                  </span>
                </label>
                <label className="flex items-center p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                  <input
                    type="radio"
                    value="manual"
                    checked={escalationType === 'manual'}
                    onChange={(e) => setEscalationType(e.target.value)}
                    className="h-4 w-4 text-orange-600 focus:ring-orange-500"
                    disabled={actionLoading}
                  />
                  <span className="ml-3 flex-1">
                    <span className="text-sm font-medium text-gray-900 block">Manual Escalation</span>
                    <span className="text-xs text-gray-500">Select specific higher-level agent for escalation</span>
                  </span>
                </label>
              </div>
            </div>

            {/* Agent Selection for Manual Escalation */}
            {escalationType === 'manual' && (
              <div className="mb-5">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Select Higher Level Agent
                </label>
                <select
                  value={selectedAgent}
                  onChange={(e) => setSelectedAgent(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-4 focus:ring-orange-200 focus:border-orange-500 transition-all"
                  disabled={actionLoading}
                >
                  <option value="">-- Select Agent --</option>
                  {Array.isArray(escalationAgents) && escalationAgents.map(agent => (
                    <option key={agent.id} value={agent.id}>
                      {agent.name} (Level {agent.staff_level})
                      {agent.current_workload !== undefined && ` - ${agent.current_workload} tickets`}
                    </option>
                  ))}
                </select>
                {(!escalationAgents || escalationAgents.length === 0) && (
                  <p className="text-xs text-gray-500 mt-1">No higher-level agents available for manual escalation</p>
                )}
              </div>
            )}

            {/* Escalation Reason */}
            <div className="mb-6">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Escalation Reason *
              </label>
              <textarea
                value={escalationReason}
                onChange={(e) => setEscalationReason(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-4 focus:ring-orange-200 focus:border-orange-500 transition-all"
                rows={4}
                placeholder="Explain why this ticket needs escalation..."
                disabled={actionLoading}
                maxLength={500}
              />
              <p className="text-xs text-gray-500 mt-1">{escalationReason.length}/500 characters</p>
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowEscalateModal(false);
                  setEscalationReason('');
                  setEscalationType('auto');
                  setSelectedAgent('');
                }}
                disabled={actionLoading}
                className="px-5 py-2.5 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmEscalation}
                disabled={actionLoading || !escalationReason.trim() || (escalationType === 'manual' && !selectedAgent)}
                className="px-5 py-2.5 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 font-medium transition-colors"
              >
                {actionLoading ? 'Escalating...' : `${escalationType === 'auto' ? 'Auto ' : 'Manual '}Escalate`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* NEW: Admin Reassign Modal */}
      {showAdminReassignModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl p-6 max-w-md w-full shadow-2xl">
            <h3 className="text-xl font-semibold mb-6 text-gray-900">Admin Reassign Escalated Ticket</h3>
            
            <div className="mb-5">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Reassign To *
              </label>
              <select
                value={selectedAgent}
                onChange={(e) => setSelectedAgent(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-4 focus:ring-purple-200 focus:border-purple-500 transition-all"
                disabled={actionLoading}
              >
                <option value="">-- Select Agent --</option>
                {Array.isArray(escalationAgents) && escalationAgents.map(agent => (
                  <option key={agent.id} value={agent.id}>
                    {agent.name} (Level {agent.staff_level})
                    {agent.current_workload !== undefined && ` - ${agent.current_workload} tickets`}
                  </option>
                ))}
              </select>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Reason for Reassignment *
              </label>
              <textarea
                value={reassignReason}
                onChange={(e) => setReassignReason(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-4 focus:ring-purple-200 focus:border-purple-500 transition-all"
                rows={3}
                placeholder="Why are you reassigning this escalated ticket?"
                disabled={actionLoading}
                maxLength={500}
              />
              <p className="text-xs text-gray-500 mt-1">{reassignReason.length}/500 characters</p>
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowAdminReassignModal(false);
                  setSelectedAgent('');
                  setReassignReason('');
                }}
                disabled={actionLoading}
                className="px-5 py-2.5 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmAdminReassign}
                disabled={actionLoading || !selectedAgent || !reassignReason.trim()}
                className="px-5 py-2.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 font-medium transition-colors"
              >
                {actionLoading ? 'Reassigning...' : 'Reassign'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* UPDATED: Enhanced Forward Modal with Return Forward Support */}
      {showForwardModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl p-6 max-w-md w-full shadow-2xl">
            <h3 className="text-xl font-semibold mb-6 text-gray-900">
              {isReturnForward ? 'Return Ticket' : 'Forward Ticket'}
            </h3>
            
            <div className="mb-5">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                {isReturnForward ? 'Return To' : 'Forward To'}
              </label>
              <select
                value={selectedAgent}
                onChange={(e) => setSelectedAgent(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-4 focus:ring-purple-200 focus:border-purple-500 transition-all"
                disabled={actionLoading}
              >
                <option value="">-- Select Agent --</option>
                {Array.isArray(availableAgents) && availableAgents.map(agent => (
                  <option key={agent.id} value={agent.id}>
                    {agent.name} (Level {agent.staff_level})
                  </option>
                ))}
              </select>
            </div>

            {/* NEW: Return Forward Checkbox */}
            <div className="mb-5">
              <label className="flex items-start">
                <input
                  type="checkbox"
                  checked={isReturnForward}
                  onChange={(e) => setIsReturnForward(e.target.checked)}
                  className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded mt-1"
                  disabled={actionLoading}
                />
                <span className="ml-3 block">
                  <span className="text-sm font-medium text-gray-700">Mark as Return Forward</span>
                  <span className="text-xs text-gray-500 block mt-1">
                    Check this if you're returning the ticket to an agent who previously worked on it
                  </span>
                </span>
              </label>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Reason for {isReturnForward ? 'Returning' : 'Forwarding'} *
              </label>
              <textarea
                value={forwardReason}
                onChange={(e) => setForwardReason(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-4 focus:ring-purple-200 focus:border-purple-500 transition-all"
                rows={3}
                placeholder={`Why are you ${isReturnForward ? 'returning' : 'forwarding'} this ticket?`}
                disabled={actionLoading}
                maxLength={500}
              />
              <p className="text-xs text-gray-500 mt-1">{forwardReason.length}/500 characters</p>
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowForwardModal(false);
                  setSelectedAgent('');
                  setForwardReason('');
                  setIsReturnForward(false);
                }}
                disabled={actionLoading}
                className="px-5 py-2.5 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmForward}
                disabled={actionLoading || !selectedAgent || !forwardReason.trim()}
                className={`px-5 py-2.5 text-white rounded-lg font-medium transition-colors disabled:opacity-50 ${
                  isReturnForward 
                    ? 'bg-blue-600 hover:bg-blue-700' 
                    : 'bg-purple-600 hover:bg-purple-700'
                }`}
              >
                {actionLoading ? 
                  `${isReturnForward ? 'Returning' : 'Forwarding'}...` : 
                  `${isReturnForward ? 'Return' : 'Forward'}`
                }
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Resolve Modal */}
      {showResolveModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl p-6 max-w-md w-full shadow-2xl">
            <h3 className="text-xl font-semibold mb-6 text-gray-900">Resolve Ticket</h3>
            
            <div className="mb-6">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Resolution Notes *
              </label>
              <textarea
                value={resolutionNotes}
                onChange={(e) => setResolutionNotes(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-4 focus:ring-green-200 focus:border-green-500 transition-all"
                rows={4}
                placeholder="Describe how the issue was resolved..."
                disabled={actionLoading}
                maxLength={2000}
              />
              <p className="text-xs text-gray-500 mt-1">{resolutionNotes.length}/2000 characters</p>
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowResolveModal(false);
                  setResolutionNotes('');
                }}
                disabled={actionLoading}
                className="px-5 py-2.5 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmResolve}
                disabled={actionLoading || !resolutionNotes.trim()}
                className="px-5 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 font-medium transition-colors"
              >
                {actionLoading ? 'Resolving...' : 'Resolve'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminTicketActions;